import { Component, OnInit } from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { Set } from 'immutable';
import {
	firstValueFrom,
	map,
	Observable,
	ReplaySubject,
	takeUntil,
	tap,
} from 'rxjs';

import {
	ElementPermission,
	PermissionDependencies,
	ReverseDependencies,
} from '../../../core/domain/permission-types';
import { DcIcons } from '../../app-dc.icons';
import { DcLoaders } from '../../app-loaders';
import { PermissionConfigItemEntity } from './permission-config-item.entity';
import {
	PermissionConfig,
	PermissionConfigListEntity,
} from './permission-config-list.entity';

export type Configuration = {
	usePanel: boolean;
	columnWidth: number;
	inViewMode: boolean;
};

@Component({
	selector: 'app-permissions-config',
	templateUrl: './app-permissions-config.component.html',
	styleUrls: ['./app-permissions-config.component.scss'],
	inputs: ['permissionsConfig', 'config'],
})
export class AppPermissionsConfigComponent
	extends DcBaseComponent
	implements OnInit
{
	protected readonly EligiblePermissions = ElementPermission;
	protected readonly DcIcons = DcIcons;
	protected readonly DcLoaders = DcLoaders;

	public vo$: Observable<{
		columns: Array<PermissionConfig>;
		elements: Array<PermissionConfigItemEntity>;
		list: PermissionConfigListEntity;
		config: Configuration;
	}>;

	private readonly permissionsConfigList$ =
		new ReplaySubject<PermissionConfigListEntity>(1);

	public constructor() {
		super();
		this.cmpId = 'app-permissions-config';

		this.vo$ = combineAll({
			columns: this.permissionsConfigList$.pipe(
				map((list) => Array.from(list.availablePermissions.values()))
			),
			elements: this.permissionsConfigList$.pipe(
				map((list) => Array.from(list.elements.values()))
			),
			list: this.permissionsConfigList$,
			config: this.toObservable<Configuration>('config'),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();

		this.toObservable<PermissionConfigListEntity>('permissionsConfig')
			.pipe(
				takeUntil(this.onDestroy$),
				tap((list) => {
					this.permissionsConfigList$.next(list);
				})
			)
			.subscribe();
	}

	public async getUpdatedPermissions(): Promise<
		Map<string, PermissionConfigItemEntity>
	> {
		const list = await firstValueFrom(this.permissionsConfigList$);
		return list.elements;
	}

	/**
	 * @param key
	 * @param permission
	 * @param checked
	 *
	 * toggle element permission
	 */
	public async onToggleChange(
		key: string,
		permission: string,
		checked: boolean
	): Promise<void> {
		const currentList = await firstValueFrom(this.permissionsConfigList$);
		const elem = currentList.elements.get(key);

		if (!elem) {
			return;
		}

		/**
		 * Détermine les permissions liées à ajouter ou supprimer.
		 *
		 * - Si la permission est activée,
		 *   récupère ses dépendances directes.
		 *
		 * - Si la permission est désactivée,
		 *   récupère les permissions qui dépendent d'elle
		 *   afin de les désactiver également.
		 */
		const dependencies = checked
			? PermissionDependencies[permission] ?? []
			: ReverseDependencies[permission] ?? [];

		/**
		 * Met à jour la liste des permissions :
		 *
		 * - `union()` ajoute la permission sélectionnée ainsi que ses dépendances.
		 * - `subtract()` supprime la permission sélectionnée ainsi que ses dépendances inverses.
		 */
		const updatedPermissions = checked
			? elem.permissions.union([permission, ...dependencies])
			: elem.permissions.subtract([permission, ...dependencies]);

		elem.updatePermissions(updatedPermissions);

		/**
		 * Met à jour l'état du toggle sur la ligne (« Tout sélectionner »).
		 *
		 * Le toggle est activé si le nombre de permissions sélectionnées
		 * sur la ligne correspond au nombre total de permissions disponibles.
		 */
		elem.updateAllToggled(
			elem.permissions.size === currentList.activePermissions.length
		);

		/**
		 * Met à jour l'élément dans la liste.
		 */
		currentList.updateElement(`${key}`, elem);

		this.permissionsConfigList$.next(currentList);
	}

	/**
	 * @param key
	 * @param checked
	 *
	 * Active ou désactive l'ensemble des permissions d'une ligne.
	 */
	public async onRowToggle(key: string, checked: boolean): Promise<void> {
		const currentList = await firstValueFrom(this.permissionsConfigList$);
		const elem = currentList.elements.get(key);

		if (!elem) {
			return;
		}

		/**
		 * Détermine la liste des permissions à appliquer :
		 *
		 * - Si le toggle est activé,
		 *   toutes les permissions disponibles sont sélectionnées.
		 *
		 * - Si le toggle est désactivé,
		 *   toutes les permissions sont supprimées.
		 */
		const allPermissions = checked ? currentList.activePermissions : [];

		/**
		 * Met à jour les permissions de l'élément.
		 */
		elem.updatePermissions(Set(allPermissions));

		/**
		 * Met à jour l'état du toggle de la ligne.
		 */
		elem.updateAllToggled(checked);

		/**
		 * Met à jour l'élément dans la liste.
		 */
		currentList.updateElement(key, elem);

		this.permissionsConfigList$.next(currentList);
	}

	/**
	 * @param permission
	 * @param checked
	 *
	 * Active ou désactive une permission pour l'ensemble des éléments.
	 */
	public async onColumnToggle(
		permission: string,
		checked: boolean
	): Promise<void> {
		const currentList = await firstValueFrom(this.permissionsConfigList$);

		/**
		 * Met à jour tous les éléments en appliquant
		 * la permission.
		 */
		currentList.updateAllElements(checked, permission);

		this.permissionsConfigList$.next(currentList);
	}
}
